import { Status, StatusDropdown } from "components";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import { recordInformation } from "components/DetailPage/RecordInformation";
import { CurrencyAndExchangeRate, CurrencyAndExchangeRatePicker, ICurrencyAndExchangeRate } from "components/exchangerate/CurrencyAndExchangeRatePicker";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate"
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import CompanyModalPicker from "components/Picker/CompanyPicker/CompanyModalPicker";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import CostCenterModalPicker from "components/Picker/CostCenterPicker/CostCenterModalPicker";
import { CostCenterLabel } from "components/Removables/RemovableCostCenter";
import { BankAccountSmallDetails, BookkeepingSmallDetails, CompanySmallDetails, ContactSmallDetails } from "components/SmallDetails";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import RequiredNumberInput from "components/ValidatedFields/RequiredNumberInput";
import moment from "moment";
import React from "react"
import { getCurrency } from "utilities";
import { getUpdateInvoiceV1DetailsInstruction } from "utilities/invoiceUtils";
import { InvoiceDueDate } from "../../components/Invoice/InvoiceDueDate";
import { InvoiceSendDate } from "../../components/Invoice/InvoiceSendDate";
import { ConvertToProforma } from "./ConvertToProforma";
import { useContact, useCustomQuery } from "utilities/hookutils";
import { useModals } from "Modals/ModalsProvider";
import VatNumberDetails from "components/VatNumber/VatNumberDetails";
import { ReverseChargeAdvice } from "./ReverseChargeAdvice";
import { InvoiceNumber } from "../../components/Invoice/InvoiceNumber";
import NoBankAccountsMessageBox from "components/MessageBox/NoBankAccountsMessageBox";
import Loading from "components/panels/loading";
import InvoiceSendDatePicker from "components/Invoice/InvoiceSendDatePicker";
import InvoiceDueDatePicker from "components/Invoice/InvoiceDueDatePicker";
import { PaymentTermInvoiceV1 } from "components/Invoice/PaymentTermInvoiceV1";
import { BankAccountDropdownOrWarning } from "invoice/BankAccountDropdownOrWarning";
import ChooseContact from "components/AddresseePicker/ChooseContact";

interface Props {
  viewModel: server.viewmodel.InvoiceV1DetailFrontModel
  invoice: server.dto.InvoiceV1
  products: server.dto.ProductsByParent;
  onChange: (invoice: server.dto.InvoiceV1) => void
  euVatNumber: server.dto.EuVatNumber;
  reverseChargeAdvice: server.dto.ReverseChangeAdvices;
  minSendDate: moment.Moment;
  maxSendDate: moment.Moment;
  refetchInvoice: () => Promise<any>;
  refetchProductsAndPayments: () => Promise<any>;
}

export const InvoiceV1DetailSection = (props: Props) => {
  const invoice = props.invoice;
  const { euVatNumber, reverseChargeAdvice, minSendDate: minInvoiceDate, maxSendDate: maxInvoiceDate, refetchInvoice, refetchProductsAndPayments } = props;

  const modals = useModals();
  const displayOgm = props.viewModel.bookkeeping.companyAdmCountry === "belgium";

  const bankAccountsApi = useCustomQuery({
    queryFn: () => window.CoreApi.BankAccount.ListBankAccountSmall({
      bookkeepings: [invoice.bookkeeping.bookkeepingId],
      page: 1,
      perPage: 100
    }).then(r => r.data.data),
    queryKey: ["bankAccounts", { bookkeepingId: invoice.bookkeeping.bookkeepingId }],
    initialData: [],
  });

  const hasStatuses = site.currentEnvironment.statuses.invoice?.length > 0;
  const isLocked = invoice.locked === "locked";

  const onValueChanged = async (key: string, value: any) => {
    if (key === "currencyAndExchangeRate") {
      props.onChange({ ...invoice, currency: value.currency, exchangeRate: value.exchangeRate });
    } else if (key === "bankAccount") {
      props.onChange({ ...invoice, bankAccount: value });
    } else if (key === "invoiceDate") {
      props.onChange({ ...invoice, invoiceDate: value });
    } else if (key === "dueDate") {
      props.onChange({ ...invoice, dueInDays: value });
    } else if (key === "paymentTerm") {
      props.onChange({ ...invoice, dueInDays: value });
    } else if (key === "addressee") {
      props.onChange({ ...invoice, company: value, contact: undefined });
    } else if (key === "contact") {
      props.onChange({ ...invoice, contact: value });
    } else if (key === "owner") {
      props.onChange({ ...invoice, owner: value });
    } else if (key === "status") {
      props.onChange({ ...invoice, status: value });
    } else if (key === "concerning") {
      props.onChange({ ...invoice, concerning: value });
    } else if (key === "costCenter") {
      props.onChange({ ...invoice, costCenter: value });
    } else {
      console.warn(`Unhandled key in InvoiceV1DetailSection: ${key}`);
      console.warn(`Value: ${value}`);
    }
  }

  const onSuccessfulUpdate = async (key: string, value: any) => {
    if (key === "addressee" && value != null) {
      const newCompany = value as server.dto.CompanySmall;
      modals.displayFinancialRiskModal(newCompany.companyId);
      await refetchInvoice();
    } else if (key === "currencyAndExchangeRate") {
      await refetchProductsAndPayments();
    }
  }

  const contact = useContact(invoice.contact);
  return <DetailSectionTemplate
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={onSuccessfulUpdate}
    isInPageEditable={invoice.permissions.canModify}
    sections={[{
      key: "invoiceMainDetails",
      type: "regular",
      title: site.currentEnvironment.invoice.singular,
      fields: [
        {
          key: "proforma",
          hidden: invoice.lifeCycle !== "draft",
          label: RESX.Document.ProForma,
          render: () => <ConvertToProforma invoice={invoice} onConverted={refetchInvoice} />,
        }, {
          key: "invoiceNumber",
          hidden: invoice.lifeCycle === "draft",
          label: RESX.GeneralLabels.Number,
          render: () => <InvoiceNumber invoiceNumber={invoice.documentNumber} locked={invoice.locked} />,
        }, {
          key: "ogmCode",
          label: RESX.Financial.OgmCode,
          hidden: invoice.lifeCycle === "draft" || !displayOgm,
          render: () => <div>{invoice.belgianOgmCode}</div>
        }, {
          key: "bookkeeping",
          label: RESX.Bookkeeping.resxBookkeeping,
          render: () => <BookkeepingSmallDetails bookkeeping={invoice.bookkeeping} />,
        }, {
          key: "currencyAndExchangeRate",
          label: RESX.Financial.Currency,
          render: () => <CurrencyAndExchangeRate
            bookkeeping={invoice.bookkeeping}
            currency={getCurrency(invoice.currency)}
            exchangeRate={invoice.exchangeRate}
          />,
          inPageEdit: {
            render: ({ value, onChange, cancel }: RenderEditOptions<ICurrencyAndExchangeRate>) => <CurrencyAndExchangeRatePicker
              focusOnMount
              validatorKey="currencyAndExchangeRate"
              bookkeeping={invoice.bookkeeping}
              currency={value.currency}
              exchangeRate={value.exchangeRate}
              onChange={onChange}
              onEsc={() => cancel()}
              onEnter={(v) => onChange(v, true)}
            />,
            fullWidth: true,
            type: "confirmOrCancelButton",
            initialValue: { currency: invoice.currency, exchangeRate: invoice.exchangeRate },
            submitChange: async (values: ICurrencyAndExchangeRate) => window.CoreApi.Invoice.Update({
              ...getUpdateInvoiceV1DetailsInstruction(invoice),
              currency: values.currency,
              exchangeRate: values.exchangeRate
            }),
          },
          editDisabled: isLocked,
        }, {
          key: "bankAccount",
          label: RESX.BankAccount.resxBankAccount,
          render: () => {
            if (invoice.bankAccount != null) {
              return <BankAccountSmallDetails bankAccount={invoice.bankAccount} />;
            }
            if (bankAccountsApi.isFetching || bankAccountsApi.isLoading) {
              return <Loading />;
            }
            if (bankAccountsApi.data.length === 0) {
              return <NoBankAccountsMessageBox color="blue" />;
            }
            return <div />;
          },
          isEmpty: !invoice.bankAccount && !(bankAccountsApi.isFetching || bankAccountsApi.isLoading) && bankAccountsApi.data.length > 0,
          inPageEdit: {
            render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.BankAccountSmall>) => {
              return <BankAccountDropdownOrWarning
                bankAccountQuery={bankAccountsApi}
                bookkeepingId={invoice.bookkeeping.bookkeepingId}
                value={value}
                onChange={(ba) => {
                  onChange(ba, true);
                }}
                inPageEditing={{ cancel }}
              />
            },
            fullWidth: true,
            type: "custom",
            initialValue: invoice.bankAccount,
            submitChange: async (bankAccount: server.dto.BankAccountSmall) => window.CoreApi.Invoice.Update({
              ...getUpdateInvoiceV1DetailsInstruction(invoice),
              bankAccountId: bankAccount.bankAccountId,
            })
          },
          editDisabled: isLocked || (bankAccountsApi.data.length === 0 && invoice.bankAccount == null),
        }, {
          key: "invoiceDate",
          label: RESX.DateTime.SendDate,
          render: () => <InvoiceSendDate invoiceDate={moment.utc(invoice.invoiceDate)} lifeCycle={invoice.lifeCycle} hasProducts={props.products.collection.length > 0} />,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }: RenderEditOptions<moment.Moment>) =>
              <InvoiceSendDatePicker
                value={value}
                onChange={onChange}
                dueInDays={invoice.dueInDays}
                minDate={moment.utc(minInvoiceDate)}
                maxDate={moment.utc(maxInvoiceDate)}
                inPageEdit={{
                  cancel,
                  submit
                }}
              />
            ,
            type: "confirmOrCancelButtonInline",
            initialValue: moment.utc(invoice.invoiceDate),
            submitChange: (receivedOn: moment.Moment) => window.CoreApi.Invoice.Update({
              ...getUpdateInvoiceV1DetailsInstruction(invoice),
              invoiceDate: receivedOn.toDate(),
            }),
          },
          editDisabled: isLocked,
        }, {
          key: "dueDate",
          label: RESX.DateTime.DueDate,
          render: () => <InvoiceDueDate invoiceDate={moment.utc(invoice.invoiceDate)} dueInDays={invoice.dueInDays} lifeCycle={invoice.lifeCycle} hasProducts={props.products.collection.length > 0} />,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }: RenderEditOptions<number>) =>
              <InvoiceDueDatePicker
                sendDate={moment.utc(invoice.invoiceDate)}
                dueInDays={value}
                onChange={(_, dueInDays, fromCalendar) => onChange(dueInDays, fromCalendar)}
                inPageEdit={{
                  cancel,
                  submit
                }}
              />,
            type: "confirmOrCancelButtonInline",
            initialValue: invoice.dueInDays,
            submitChange: (dueInDays: number) => window.CoreApi.Invoice.Update({
              ...getUpdateInvoiceV1DetailsInstruction(invoice),
              dueInDays,
            }),
          },
          editDisabled: isLocked,
        }, {
          key: "paymentTerm",
          label: RESX.DateTime.Term,
          render: () => <PaymentTermInvoiceV1 dueInDays={invoice.dueInDays} />,
          inPageEdit: {
            render: ({ value, onChange, cancel }: RenderEditOptions<number>) => {
              return <RequiredNumberInput
                autoFocus
                width={60}
                validatorKey="dueInDays"
                value={value}
                onChange={(v: number) => onChange(v)}
                onEnterPressed={(v: number) => onChange(v, true)}
                onEscPressed={() => cancel()}
                decimalPoints={0}
                labelContent={RESX.DateTime.days}
                minValue={0}
                maxValue={serverReference.constants.invoice.maxDueInDays}
              />;
            },
            type: "confirmOrCancelButton",
            initialValue: invoice.dueInDays,
            submitChange: (dueInDays: number) => window.CoreApi.Invoice.Update({
              ...getUpdateInvoiceV1DetailsInstruction(invoice),
              dueInDays,
            })
          },
          editDisabled: isLocked,
        }, {
          key: "addressee",
          label: RESX.Document.Addressee,
          render: () => <CompanySmallDetails company={invoice.company} layout="multiLine" />,
          inPageEdit: {
            render: ({ onChange }) => <div className="main-section-template-fields-padding">
              <CompanyModalPicker
                startOpen
                value={null}
                onChange={v => onChange(v, true)}
                modalTitle={RESX.Company.resxCompany}
                addressType="administrative"
                validatorKey="addressee"
                required={false}
                canCreateNew={site.permissions.addressBook.canCreate}
                layout="multiLine"
              />
            </div>,
            fullWidth: false,
            type: "custom",
            initialValue: null,
            submitChange: async (company: server.dto.CompanySmall) => window.CoreApi.Invoice.Update({
              ...getUpdateInvoiceV1DetailsInstruction(invoice),
              addresseeCompanyId: company.companyId,
              contactId: undefined
            })
          },
          editDisabled: isLocked,
        }, {
          key: "contact",
          label: RESX.Contact.resxContact,
          isEmpty: contact == null,
          render: () => <ContactSmallDetails contact={contact} />,
          inPageEdit: {
            render: ({ value, onChange }) => <div className="main-section-template-fields-padding">
              <ChooseContact
                company={invoice.company}
                selectedContact={value}
                onContactChanged={(contact) => onChange(contact, true)}
              />
            </div>,
            fullWidth: false,
            type: "custom",
            initialValue: invoice.contact,
            submitChange: async (contact: server.dto.ContactSmallV2) => window.CoreApi.Invoice.Update({
              ...getUpdateInvoiceV1DetailsInstruction(invoice),
              contactId: contact?.contactId,
            })
          },
          editDisabled: isLocked,
        }, {
          key: "concerning",
          label: RESX.InvoiceDocument.Concerning,
          isEmpty: !invoice.concerning,
          render: () => <OptionalTextAreaDetails>{invoice.concerning}</OptionalTextAreaDetails>,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
              focusOnMount
              maxLength={serverReference.constants.document.maxConcerningLength}
              value={value}
              onChange={onChange}
              onCtrlEnter={submit}
              onEsc={cancel}
            />,
            fullWidth: true,
            type: "confirmOrCancelButton",
            initialValue: invoice.concerning,
            submitChange: (concerning: string) => window.CoreApi.Invoice.Update({
              ...getUpdateInvoiceV1DetailsInstruction(invoice),
              concerning,
            }),
          },
          editDisabled: isLocked,
        }, {
          key: "costCenter",
          label: RESX.CostCenter.resxCostCenter,
          hidden: !site.currentEnvironment.hasCostCenters,
          isEmpty: !invoice.costCenter,
          render: () => <CostCenterLabel value={invoice.costCenter} />,
          inPageEdit: {
            render: ({ onChange }) => <div className="main-section-template-fields-padding">
              <CostCenterModalPicker
                canCreateNew={false}
                startOpen
                value={null}
                onChange={v => onChange(v, true)}
                validatorKey="costCenter"
                required={false}
              />
            </div>,
            fullWidth: false,
            type: "custom",
            initialValue: invoice.costCenter,
            submitChange: async (costCenter: server.dto.CostCenter) => window.CoreApi.Invoice.Update({
              ...getUpdateInvoiceV1DetailsInstruction(invoice),
              costCenterId: costCenter.costCenterId,
            })
          },
          editDisabled: isLocked,
        }, {
          key: "status",
          hidden: !hasStatuses,
          label: RESX.Status.resxStatus,
          render: () => <Status status={invoice.status} />,
          inPageEdit: {
            render: ({ value, onChange }) => (
              <StatusDropdown
                value={value}
                onChange={(v) => onChange(v, true)}
                mainArea="invoice"
                options={undefined}
                config={{
                  focusOnMount: true,
                  openMenuOnFocus: true
                }}
              />
            ),
            type: "confirmOrCancelButton",
            initialValue: invoice.status,
            fullWidth: true,
            submitChange: (value: server.dto.StatusSmall) => {
              return window.CoreApi.Invoice.Update({
                ...getUpdateInvoiceV1DetailsInstruction(invoice),
                statusId: value.statusId,
              });
            }
          }
        }, {
          key: "owner",
          label: RESX.GeneralLabels.Owner,
          render: () => <UserWithAvatar user={invoice.owner} />,
          inPageEdit: {
            render: ({ onChange }) => <div className="main-section-template-fields-padding">
              <UserModalPicker
                startOpen
                value={null}
                onChange={v => onChange(v, true)}
                validatorKey="owner"
                required={false}
                canCreateNew={false}
              />
            </div>,
            fullWidth: false,
            type: "custom",
            initialValue: invoice.owner,
            submitChange: async (owner: server.dto.UserSmall) => window.CoreApi.Invoice.Update({
              ...getUpdateInvoiceV1DetailsInstruction(invoice),
              ownerUserId: owner.userId,
            }),
          },
          editDisabled: isLocked,
        }]
    },
    {
      key: "reverseCharge",
      title: RESX.ReverseCharge.xReverseCharge,
      type: "regular",
      fields: [{
        key: "euVatNumber",
        label: RESX.VatNumber.EuVatNumber,
        hidden: !euVatNumber.isEuMember,
        render: () => <VatNumberDetails key={euVatNumber.number} euVatNumber={euVatNumber} companyId={invoice.company.companyId} />,
      }, {
        key: "reverseChargeAdvice",
        label: RESX.ReverseCharge.Advice,
        render: () => <ReverseChargeAdvice advice={reverseChargeAdvice} />,
      }]
    },
    recordInformation(
      invoice.createdUtc,
      invoice.owner,
      invoice.lastModifiedUtc,
      props.viewModel.lastModifiedUser
    )]}
  />
}